import React, { useRef, useState } from "react";
import { Loader2, CheckCircle2, Upload, FileText, X, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { track } from "../lib/analytics";
import { useToast } from "../hooks/use-toast";
import { brand } from "../data/site";
import { careerRoles, experienceLevels, employmentTypes, usStates, RESUME_MAX_MB, RESUME_TYPES } from "../data/careers";

const APPLY_ENDPOINT = "/api/apply";

const empty = {
  firstName: "", lastName: "", email: "", phone: "", street: "", city: "", state: "AZ", zip: "",
  role: "", experience: "", employmentType: "", startDate: "",
  authorized: "", driversLicense: "", link: "", message: "", company: "",
};

const labelCls = "block text-xs font-medium mb-1.5 uppercase tracking-wide text-ink/80";
const inputCls =
  "w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors bg-white border-ink/15 text-ink placeholder:text-ink/60 focus:border-brand";

const Select = ({ id, value, onChange, placeholder, options, required, compact = false, autoComplete }) => (
  <div className="relative">
    <select
      id={id}
      value={value}
      required={required}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputCls} appearance-none ${compact ? "pl-3 pr-7" : "pr-10"} ${value ? "" : "text-ink/60"}`}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o} value={o} className="text-ink">{o}</option>
      ))}
    </select>
    <ChevronDown size={16} className={`absolute ${compact ? "right-2" : "right-3"} top-1/2 -translate-y-1/2 text-ink/50 pointer-events-none`} />
  </div>
);

const YesNo = ({ name, legend, value, onChange, required }) => (
  <fieldset>
    <legend className={labelCls}>{legend}{required ? " *" : ""}</legend>
    <div className="grid grid-cols-2 gap-2.5">
      {["Yes", "No"].map((opt) => (
        <label
          key={opt}
          className={`flex items-center justify-center gap-2 rounded-md border px-3 py-3 text-sm font-medium cursor-pointer transition-all has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring ${
            value === opt ? "bg-brand border-brand text-ink" : "bg-white border-ink/15 text-ink/75 hover:border-brand"
          }`}
        >
          <input type="radio" name={name} value={opt} checked={value === opt} onChange={() => onChange(opt)} className="sr-only" />
          {opt}
        </label>
      ))}
    </div>
  </fieldset>
);

const ApplyForm = () => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [form, setForm] = useState(empty);
  const [resume, setResume] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const pickFile = (file) => {
    if (!file) return;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!RESUME_TYPES.includes(ext)) {
      toast({ title: "Unsupported file type", description: "Please upload your resume as a PDF, DOC or DOCX file." });
      return;
    }
    if (file.size > RESUME_MAX_MB * 1024 * 1024) {
      toast({ title: "File is too large", description: `Resumes must be ${RESUME_MAX_MB} MB or smaller.` });
      return;
    }
    setResume(file);
  };

  const clearFile = () => {
    setResume(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const f = form;
    if (!f.firstName.trim() || !f.lastName.trim() || !f.email.trim() || !f.phone.trim()) {
      toast({ title: "Please complete required fields", description: "Name, email, and phone are required." });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) {
      toast({ title: "Check your email", description: "Please enter a valid email address." });
      return;
    }
    if (f.phone.replace(/\D/g, "").length < 10) {
      toast({ title: "Check your phone number", description: "Please enter a 10-digit phone number." });
      return;
    }
    if (!f.street.trim() || !f.city.trim() || !/^[A-Z]{2}$/.test(f.state) || !/^\d{5}$/.test(f.zip)) {
      toast({ title: "Add your address", description: "Please enter your street address, city, state and 5-digit ZIP." });
      return;
    }
    if (!f.role) {
      toast({ title: "Choose a role", description: "Select the position you're interested in." });
      return;
    }
    if (!f.authorized) {
      toast({ title: "One more question", description: "Let us know whether you're authorized to work in the US." });
      return;
    }
    if (f.link && !/^https?:\/\/\S+$/i.test(f.link.trim())) {
      toast({ title: "Check your link", description: "Links should start with https://" });
      return;
    }
    if (!resume) {
      toast({ title: "Resume required", description: "Please attach your resume (PDF, DOC or DOCX)." });
      return;
    }

    const body = new FormData();
    Object.entries(f).forEach(([k, v]) => body.append(k, v));
    body.append("resume", resume);

    setSubmitting(true);
    try {
      const res = await fetch(APPLY_ENDPOINT, { method: "POST", body });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      setSubmitted(true);
      toast({ title: "Application sent!", description: "Thanks for your interest in Pepla." });
      track("submit_application");
    } catch {
      toast({
        title: "Your application didn't go through",
        description: `Please try again, or call us at ${brand.phone}.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-2xl p-10 text-center bg-secondary border border-ink/10">
        <CheckCircle2 size={56} className="text-brand mx-auto mb-4" />
        <h3 className="font-heading text-2xl uppercase tracking-wide mb-2 text-ink">Application Received</h3>
        <p className="text-ink/70">
          Thanks, {form.firstName}. Our team will review your application for {form.role} and reach out if it's a fit.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate encType="multipart/form-data">
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <label>
          Company
          <input tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => update("company", e.target.value)} />
        </label>
      </div>

      {/* Contact details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="ap-first" className={labelCls}>First Name *</label>
          <input id="ap-first" autoComplete="given-name" value={form.firstName} onChange={(e) => update("firstName", e.target.value)} className={inputCls} placeholder="John" />
        </div>
        <div>
          <label htmlFor="ap-last" className={labelCls}>Last Name *</label>
          <input id="ap-last" autoComplete="family-name" value={form.lastName} onChange={(e) => update("lastName", e.target.value)} className={inputCls} placeholder="Smith" />
        </div>
        <div>
          <label htmlFor="ap-email" className={labelCls}>Email *</label>
          <input id="ap-email" type="email" autoComplete="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} placeholder="you@email.com" />
        </div>
        <div>
          <label htmlFor="ap-phone" className={labelCls}>Phone *</label>
          <input id="ap-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="(602) 555-0123" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ap-street" className={labelCls}>Street Address *</label>
          <input id="ap-street" autoComplete="street-address" value={form.street} onChange={(e) => update("street", e.target.value)} className={inputCls} placeholder="1234 W Main St, Apt 5" />
        </div>
        <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-[minmax(0,1fr)_5.5rem_6.5rem] gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label htmlFor="ap-city" className={labelCls}>City *</label>
            <input id="ap-city" autoComplete="address-level2" value={form.city} onChange={(e) => update("city", e.target.value)} className={inputCls} placeholder="Phoenix" />
          </div>
          <div>
            <label htmlFor="ap-state" className={labelCls}>State *</label>
            <Select id="ap-state" value={form.state} onChange={(v) => update("state", v)} options={usStates} required compact autoComplete="address-level1" />
          </div>
          <div>
            <label htmlFor="ap-zip" className={labelCls}>ZIP *</label>
            <input id="ap-zip" autoComplete="postal-code" inputMode="numeric" maxLength={5} value={form.zip} onChange={(e) => update("zip", e.target.value.replace(/\D/g, "").slice(0, 5))} className={inputCls} placeholder="85018" />
          </div>
        </div>
      </div>

      {/* Role */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label htmlFor="ap-role" className={labelCls}>Role You're Interested In *</label>
          <Select id="ap-role" value={form.role} onChange={(v) => update("role", v)} placeholder="Select a role" options={careerRoles} required />
        </div>
        <div>
          <label htmlFor="ap-exp" className={labelCls}>Years of Experience</label>
          <Select id="ap-exp" value={form.experience} onChange={(v) => update("experience", v)} placeholder="Select" options={experienceLevels} />
        </div>
        <div>
          <label htmlFor="ap-type" className={labelCls}>Looking For</label>
          <Select id="ap-type" value={form.employmentType} onChange={(v) => update("employmentType", v)} placeholder="Select" options={employmentTypes} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ap-start" className={labelCls}>When Can You Start?</label>
          <input id="ap-start" value={form.startDate} onChange={(e) => update("startDate", e.target.value)} className={inputCls} placeholder="e.g. Immediately, two weeks' notice" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <YesNo name="authorized" legend="Authorized to work in the US?" value={form.authorized} onChange={(v) => update("authorized", v)} required />
        <YesNo name="driversLicense" legend="Valid driver's license?" value={form.driversLicense} onChange={(v) => update("driversLicense", v)} />
      </div>

      {/* Resume */}
      <div>
        <span className={labelCls} id="ap-resume-label">Resume *</span>
        {resume ? (
          <div className="flex items-center gap-3 rounded-md border border-brand bg-brand/10 px-4 py-4">
            <FileText size={22} className="text-brand shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{resume.name}</div>
              <div className="text-xs text-ink/60">{resume.size < 1024 * 1024 ? `${Math.max(1, Math.round(resume.size / 1024))} KB` : `${(resume.size / 1024 / 1024).toFixed(1)} MB`}</div>
            </div>
            <button type="button" onClick={clearFile} aria-label="Remove resume" className="p-1.5 rounded-md hover:bg-ink/5 text-ink/60 hover:text-ink">
              <X size={18} />
            </button>
          </div>
        ) : (
          <label
            htmlFor="ap-resume"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files?.[0]); }}
            className={`flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-8 text-center cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring ${
              dragging ? "border-brand bg-brand/10" : "border-ink/15 hover:border-brand bg-white"
            }`}
          >
            <span className="w-12 h-12 rounded-xl bg-ink flex items-center justify-center">
              <Upload size={22} className="text-brand" />
            </span>
            <span className="text-sm font-medium">Upload your resume or drag it here</span>
            <span className="text-xs text-ink/60">PDF, DOC or DOCX, up to {RESUME_MAX_MB} MB</span>
            <input
              ref={fileRef}
              id="ap-resume"
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              aria-labelledby="ap-resume-label"
              onChange={(e) => pickFile(e.target.files?.[0])}
              className="sr-only"
            />
          </label>
        )}
      </div>

      <div>
        <label htmlFor="ap-link" className={labelCls}>LinkedIn or Portfolio (optional)</label>
        <input id="ap-link" type="url" value={form.link} onChange={(e) => update("link", e.target.value)} className={inputCls} placeholder="https://" />
      </div>

      <div>
        <label htmlFor="ap-msg" className={labelCls}>Tell Us About Yourself (optional)</label>
        <textarea
          id="ap-msg"
          rows={5}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          className={`${inputCls} resize-none`}
          placeholder="Relevant experience, certifications, trades you know, the areas you can work in..."
        />
      </div>

      <p className="text-[11px] leading-relaxed text-ink/60">
        By applying, you agree that Pepla Roofing may contact you about employment opportunities.
        Your application is sent directly to our hiring team. See our{" "}
        <Link to="/privacy-policy" className="underline hover:text-brand">Privacy Policy</Link>.
      </p>

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-6 py-4 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : "Submit Application"}
      </button>
    </form>
  );
};

export default ApplyForm;
